/* eslint-disable @typescript-eslint/no-explicit-any */
import { SOL_CHAIN_ID } from '../../../utils/constants';
import { AbstractWalletConnector } from '../../core/AbstractWalletConnector';
import { ChainTypes } from '../../core/types';
import { addListener, isBrowser } from '../../misc';

export class SolanaConnector extends AbstractWalletConnector {
  readonly chainType = ChainTypes.SOL as const;

  private _provider?: any;
  public get provider() {
    return this._provider;
  }

  private _getProvider: () => Promise<any>;

  constructor(name: string, _provider: unknown) {
    super(name);
    this.chainId = SOL_CHAIN_ID;
    this._getProvider = async () => {
      const provider = typeof _provider === 'function' ? await _provider() : _provider;
      if (!provider) {
        return;
      }
      if (typeof provider === 'string') {
        return isBrowser ? window[provider] : undefined;
      }
      if (typeof provider === 'object') {
        return provider;
      }
      return;
    };
  }
  private _setPublicKey(key: any) {
    if (!key) {
      this.address = undefined;
    } else {
      try {
        const address = key.toBase58();
        this.publicKey = key.toBuffer().toString('hex');
        this.address = address;
      } catch (e) {
        console.error(e);
        this.address = undefined;
      }
    }
  }

  private _unSubscribeProvider: (() => void) | undefined;
  private _subscribeProvider(provider: any) {
    this._unSubscribeProvider?.();
    if (!provider.on) {
      return;
    }
    const removeAccountChange = addListener(provider, 'accountChanged', (publicKey: any) => {
      this._setPublicKey(publicKey);
    });
    const removeDisconnect = addListener(provider, 'disconnect', () => {
      this._setPublicKey(undefined);
    });

    const removeOnConnect = addListener(provider, 'connect', async () => {
      const { publicKey } = await provider.connect();
      this._setPublicKey(publicKey);
    });

    const unSubscribe = () => {
      removeAccountChange();
      removeOnConnect();
      removeDisconnect();
      this._unSubscribeProvider = undefined;
    };
    this._unSubscribeProvider = unSubscribe;
  }

  // solana 暂时不支持 悄悄恢复钱包
  async connect(_recovery = false) {
    const provider = await this.getProvider();
    if (!provider) {
      throw new Error(`${this.name} provider not found or not ready`);
    }
    const { publicKey } = await provider.connect();
    this._setPublicKey(publicKey);
    return this.address;
  }

  async disconnect() {
    this._setPublicKey(undefined);
    this.publicKey = undefined;
    const provider = (await this.getProvider()) as any;
    if (typeof provider?.disconnect === 'function') {
      await provider?.disconnect();
    }
    return;
  }

  async getProvider(force = false): Promise<any> {
    if (!this._provider || force) {
      const eipProvider = await this._getProvider();
      if (eipProvider) {
        this._provider = eipProvider;
        this._subscribeProvider(eipProvider);
      } else {
        this._provider = undefined;
      }
    }
    return this._provider;
  }

  async isAvailable() {
    const provider = await this.getProvider();
    return Boolean(provider);
  }
}
